import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { setError, setLoaded, setLoading, updateState, withCallState } from '@angular-architects/ngrx-toolkit';
import { Subscription } from 'rxjs';
import {
  ProjectJourneyEvent,
  ProjectJourneyHistory,
  ProjectJourneySnapshot,
  ReprocessRecognitionRequest,
  ReprocessRecognitionResult,
} from '@lite-workspace/dto-fotoclass-common';

import { ProjectJourneyApi } from '../infrastructure/project-journey.api';
import { PROJECT_JOURNEY_SOCKET } from '../infrastructure/project-journey-socket.port';
import {
  applyEventToUnits,
  emptyUnits,
  isEventNewerThanSnapshot,
  isSnapshotOwnedUnit,
  mergeJourneyHistoryIntoFeed,
  UnitsState,
  unitsFromSnapshot,
} from '../entities/project-journey.types';

const FEED_LIMIT = 200;

interface ProjectJourneyState {
  projectId: string | null;
  generatedAt: string | null;
  units: UnitsState;
  feed: ProjectJourneyEvent[];
}

const initialState: ProjectJourneyState = {
  projectId: null,
  generatedAt: null,
  units: emptyUnits(),
  feed: [],
};

export function withProjectJourney() {
  return signalStoreFeature(
    withState<ProjectJourneyState>(initialState),
    withCallState({ collections: ['snapshot'] }),
    withComputed(() => {
      const socket = inject(PROJECT_JOURNEY_SOCKET);
      return { connected: socket.connected };
    }),
    withMethods(
      (store, api = inject(ProjectJourneyApi), socket = inject(PROJECT_JOURNEY_SOCKET), destroyRef = inject(DestroyRef)) => {
        // Generation stamp: guards a project switch racing an in-flight snapshot fetch (both the
        // first load and a reconnect re-baseline) — a stale response must never land over a fresher
        // selection. Reconnect re-fetches reuse the CURRENT generation (they don't change "which
        // project"), so a project switch invalidates any reconnect fetch still in flight for the old one.
        let gen = 0;
        let roomSub: Subscription | null = null;
        // Non-null exactly while a snapshot request is in flight: live events arriving in that window
        // are buffered here instead of mutating counters, so a slow snapshot (the authority) can never
        // be double-counted against events it already reflects — see async-state-mutation.md.
        let inFlightBuffer: ProjectJourneyEvent[] | null = null;

        function leaveCurrentRoom(): void {
          roomSub?.unsubscribe();
          roomSub = null;
          const projectId = store.projectId();
          if (projectId) socket.leaveRoom(projectId);
        }

        function applyEvent(event: ProjectJourneyEvent): void {
          inFlightBuffer?.push(event);
          updateState(store, '[ProjectJourney] event', state => ({
            units: inFlightBuffer ? state.units : applyEventToUnits(state.units, event),
            feed: [event, ...state.feed].slice(0, FEED_LIMIT),
          }));

          // This unit's counters are backend-owned state, so the event is a cue to re-read rather than
          // an addend. A fetch already in flight will bring the fresh truth on its own — no second one.
          if (isSnapshotOwnedUnit(event.unit) && !inFlightBuffer) {
            const projectId = store.projectId();
            if (projectId) void resyncFromSnapshot(projectId, gen);
          }
        }

        async function resyncFromSnapshot(projectId: string, myGen: number): Promise<void> {
          const buffer: ProjectJourneyEvent[] = [];
          inFlightBuffer = buffer;
          try {
            updateState(store, '[ProjectJourney] snapshot', setLoading('snapshot'));
            const snapshot: ProjectJourneySnapshot = await api.getSnapshot(projectId);
            if (myGen !== gen) return;

            // Two resyncs (an event-triggered one, a reconnect, a manual reload) can be in flight at
            // once under the same generation — `myGen !== gen` only rules out a project switch, it does
            // not order these against each other. Whichever snapshot is chronologically newest must win,
            // not whichever response happens to land last.
            const currentGeneratedAt = store.generatedAt();
            if (
              currentGeneratedAt !== null &&
              new Date(snapshot.generatedAt).getTime() < new Date(currentGeneratedAt).getTime()
            ) {
              return;
            }

            // Replay only events strictly newer than the snapshot: anything at or before generatedAt
            // is presumed already folded into its counters (replaying it too would double-count).
            let units = unitsFromSnapshot(snapshot);
            for (const event of buffer) {
              if (isEventNewerThanSnapshot(event, snapshot.generatedAt)) {
                units = applyEventToUnits(units, event);
              }
            }

            updateState(store, '[ProjectJourney] snapshot success', { units, generatedAt: snapshot.generatedAt });
            updateState(store, '[ProjectJourney] snapshot success', setLoaded('snapshot'));
          } catch (error) {
            if (myGen !== gen) return;
            updateState(store, '[ProjectJourney] snapshot failure', setError(error, 'snapshot'));
          } finally {
            if (inFlightBuffer === buffer) inFlightBuffer = null;
          }
        }

        // Best-effort backfill: the live feed already builds itself from socket events, so a failed
        // or stale (project switched mid-fetch) history page just leaves the feed as the socket built it.
        async function loadHistory(projectId: string, myGen: number): Promise<void> {
          try {
            const history: ProjectJourneyHistory = await api.getHistory(projectId, FEED_LIMIT);
            if (myGen !== gen) return;

            updateState(store, '[ProjectJourney] history', state => ({
              feed: mergeJourneyHistoryIntoFeed(state.feed, history.events).slice(0, FEED_LIMIT),
            }));
          } catch {
            /* empty */
          }
        }

        socket.reconnected$.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
          const projectId = store.projectId();
          if (!projectId) return;
          void resyncFromSnapshot(projectId, gen);
        });

        return {
          async selectProject(projectId: string): Promise<void> {
            const trimmed = projectId.trim();
            if (!trimmed || store.projectId() === trimmed) return;

            leaveCurrentRoom();
            const myGen = ++gen;
            updateState(store, '[ProjectJourney] selectProject', {
              projectId: trimmed,
              generatedAt: null,
              units: emptyUnits(),
              feed: [],
            });

            socket.connect();
            roomSub = socket.joinRoom(trimmed).subscribe(event => {
              if (myGen !== gen) return;
              applyEvent(event);
            });

            await Promise.all([resyncFromSnapshot(trimmed, myGen), loadHistory(trimmed, myGen)]);
          },
          // Reuses the CURRENT generation (like the reconnect handler) instead of bumping it: a
          // bump here would desync the still-active room subscription's guard and silently drop
          // every later live event for a room nothing actually left.
          async reloadSnapshot(): Promise<void> {
            const projectId = store.projectId();
            if (!projectId) return;
            await resyncFromSnapshot(projectId, gen);
          },
          clearProject(): void {
            leaveCurrentRoom();
            gen++;
            updateState(store, '[ProjectJourney] clearProject', initialState);
          },
          // Операторский reprocess распознавания (FKS-11457). Счётчики канваса обновятся сокетом,
          // но боевой вызов сразу перечитывает снапшот, чтобы оператор увидел сдвиг без ожидания.
          async reprocessRecognition(request: ReprocessRecognitionRequest): Promise<ReprocessRecognitionResult> {
            const projectId = store.projectId();
            if (!projectId) throw new Error('No project selected');

            const result = await api.reprocessRecognition(projectId, request);
            if (!request.dryRun) void resyncFromSnapshot(projectId, gen);
            return result;
          },
        };
      },
    ),
  );
}
